import type {
  AutoCampaignRow,
  AutoChartFamily,
  AutoCostPer,
  AutoCountKey,
  AutoCounts,
  AutoCreativeCard,
  AutoMoney,
  MetaAutoBucket,
  PortalAutoHeadline,
  PortalAutoPanel,
  PortalMetaAutomotiveReport,
} from '../../types/portalAutomotive.js';
import { META_AUTO_BUCKETS, isMetaAutoBucket } from '../../types/portalAutomotive.js';
import type { PortalCoverage, PortalGrouping } from '../../types/portalReport.js';
import { creativeIdentityKey, resolveMergedStatus } from './creativeGrouping.js';
import { buildSeries, type MeasuredBucket } from './series.js';
import { yearOnYearMonths } from './yearOnYear.js';

// ⚠️⚠️ THE AUTOMOTIVE META REPORT (2026-11-11) — the pure core. PURE: no I/O.
//
// A SECOND PROFILE OF THE SAME MACHINE as the goal-split report
// (metaGoalReport.ts), not a new machine: the same reconcile rule, the same series
// builder, the same per-campaign bucket resolution. Which campaign is in which
// panel is decided in server/modules/reporting/query/campaignClass.ts
// (`META_AUTO_RULES`) and nowhere else; this file decides what each panel SHOWS.
//
// ⚠️⚠️ THE RECONCILE RULE (Scott): every panel shows EVERY result its campaigns
// produced, and a COST-PER is shown only for a goal the panel was set up for.
// Because a campaign is in exactly one bucket, spend and every count sum across
// the six panels to the independently queried headline BY CONSTRUCTION, and
// `reconcileAutomotive` checks it against that witness.
//
// ⚠️ THE ONE DELIBERATE EXCEPTION is the AIA panel. Vehicle-inventory campaigns
// run on EITHER a content-view goal or a lead goal and are kept together by NAME
// (Scott, round 2), so the panel holds both kinds of campaign and BOTH are its
// goals: it shows cost per lead AND cost per content view. ⚠️ EACH DIVIDES THE
// WHOLE PANEL SPEND, so the two are not additive and neither is "the" cost of the
// panel. No client-facing note says so (the equivalent O-Studio note was dropped
// in round 2); if the misreading turns up in a client conversation, the sentence
// is the thing to restore.
//
// ⚠️ CONTACT is not a panel. It is a headline count and a per-panel extra, with no
// cost-per (nothing optimises for it), and it is HIDDEN wherever it is zero in
// both the selected and the comparison period. Today it is always zero because
// `META_CONTACT_ACTION_KEYS` is empty; the path is built so a real key is a
// one-line change.
//
// ⚠️ NOTHING HERE NAMES A BRAND OR AN ACCOUNT. Who gets this report is
// `resolveReportTemplate` (reportTemplate.ts).

// ---------------------------------------------------------------------------
// The panel spec — the ONE place a panel's contents are defined.
// ---------------------------------------------------------------------------

/** A cost-per and the count it divides. ⚠️ The denominator is per PANEL: the AIA panel's cost per lead divides ALL leads. */
export interface AutoCostPerSpec {
  key: AutoCostPer;
  over: AutoCountKey;
}

export interface AutoChartSpec {
  key: string;
  metric: AutoCountKey;
  costPer: AutoCostPerSpec | null;
}

export interface AutoPanelSpec {
  bucket: MetaAutoBucket;
  title: string;
  /** The panel's own goal counts, in display order. */
  primary: readonly AutoCountKey[];
  costPer: readonly AutoCostPerSpec[];
  /** Counts shown WITHOUT a cost-per. ⚠️ `contacts` here is subject to hide-when-zero. */
  extras: readonly AutoCountKey[];
  /**
   * `always` renders even when empty; `when_active` renders only when the panel
   * has something in the selected OR the comparison period (round 4 §4), so a
   * panel that never applies to a client never teaches them to ignore panels.
   */
  render: 'always' | 'when_active';
  /** Rendered when `always` and empty. One sentence, so the client sees the structure every month. */
  emptySentence: string | null;
  note: string | null;
  charts: readonly AutoChartSpec[];
  /** The count creatives are ranked by. */
  creativeMetric: AutoCountKey | 'spend';
  /** Whether the campaign table (with a Goal column) is drawn. */
  campaignTable: boolean;
}

/** ⚠️ CLIENT-FACING, same wording as the goal-split page — see UNCATEGORISED_NOTE there. */
export const AUTO_UNCATEGORISED_NOTE =
  "Campaign objective isn't available in our data feed for these campaigns, so they can't be grouped by goal. All spend and results are still counted.";

const COST = {
  perLeadAll: { key: 'costPerLead', over: 'leads' } as AutoCostPerSpec,
  perMetaLead: { key: 'costPerLead', over: 'metaLeads' } as AutoCostPerSpec,
  perWebsiteLead: { key: 'costPerWebsiteLead', over: 'websiteLeads' } as AutoCostPerSpec,
  perContentView: { key: 'costPerContentView', over: 'contentViews' } as AutoCostPerSpec,
  perLpv: { key: 'costPerLandingPageView', over: 'landingPageViews' } as AutoCostPerSpec,
};

export const AUTO_PANELS: readonly AutoPanelSpec[] = [
  {
    bucket: 'aia',
    title: 'Vehicle inventory campaigns (AIA)',
    primary: ['leads', 'contentViews'],
    // ⚠️ BOTH, and each divides the WHOLE panel spend — see the header.
    costPer: [COST.perLeadAll, COST.perContentView],
    extras: ['contacts'],
    render: 'always',
    emptySentence: 'No vehicle inventory (AIA) campaigns ran in this period.',
    note: null,
    charts: [
      { key: 'contentViews', metric: 'contentViews', costPer: COST.perContentView },
      { key: 'leads', metric: 'leads', costPer: COST.perLeadAll },
    ],
    creativeMetric: 'contentViews',
    campaignTable: true,
  },
  {
    bucket: 'meta_lead',
    title: 'Meta lead campaigns',
    primary: ['metaLeads'],
    costPer: [COST.perMetaLead],
    extras: ['websiteLeads', 'contentViews', 'contacts'],
    render: 'always',
    emptySentence: 'No Meta lead campaigns ran in this period.',
    note: null,
    charts: [{ key: 'metaLeads', metric: 'metaLeads', costPer: COST.perMetaLead }],
    creativeMetric: 'metaLeads',
    campaignTable: false,
  },
  {
    bucket: 'website_lead',
    title: 'Website lead campaigns',
    primary: ['websiteLeads'],
    costPer: [COST.perWebsiteLead],
    extras: ['metaLeads', 'contentViews', 'contacts'],
    render: 'when_active',
    emptySentence: null,
    note: null,
    charts: [{ key: 'websiteLeads', metric: 'websiteLeads', costPer: COST.perWebsiteLead }],
    creativeMetric: 'websiteLeads',
    campaignTable: false,
  },
  {
    bucket: 'content_view',
    title: 'Content view campaigns',
    primary: ['contentViews'],
    costPer: [COST.perContentView],
    extras: ['websiteLeads', 'landingPageViews', 'contacts'],
    render: 'when_active',
    emptySentence: null,
    note: null,
    charts: [{ key: 'contentViews', metric: 'contentViews', costPer: COST.perContentView }],
    creativeMetric: 'contentViews',
    campaignTable: false,
  },
  {
    bucket: 'other',
    title: 'Other campaigns',
    primary: ['landingPageViews', 'impressions'],
    costPer: [COST.perLpv],
    extras: ['metaLeads', 'websiteLeads', 'contentViews', 'contacts'],
    render: 'when_active',
    emptySentence: null,
    note: null,
    charts: [{ key: 'landingPageViews', metric: 'landingPageViews', costPer: COST.perLpv }],
    creativeMetric: 'landingPageViews',
    campaignTable: false,
  },
  {
    bucket: 'uncategorised',
    title: 'Uncategorised',
    // ⚠️ NO COST-PER — there is no known goal to divide by, and that absence is the honest signal.
    primary: [],
    costPer: [],
    extras: ['metaLeads', 'websiteLeads', 'contentViews', 'contacts', 'landingPageViews'],
    render: 'when_active',
    emptySentence: null,
    note: AUTO_UNCATEGORISED_NOTE,
    charts: [],
    creativeMetric: 'spend',
    campaignTable: false,
  },
];

export function autoPanelSpecFor(bucket: MetaAutoBucket): AutoPanelSpec {
  const spec = AUTO_PANELS.find((p) => p.bucket === bucket);
  if (!spec) throw new Error(`automotiveReport: no panel spec for ${bucket}`);
  return spec;
}

export const AUTO_COUNT_LABELS: Record<AutoCountKey, string> = {
  metaLeads: 'Meta leads',
  websiteLeads: 'Website leads',
  leads: 'Leads',
  contentViews: 'Content views',
  contacts: 'Contacts',
  landingPageViews: 'Landing page views',
  impressions: 'Impressions',
};

export const AUTO_COST_PER_LABELS: Record<AutoCostPer, string> = {
  costPerLead: 'Cost per lead',
  costPerWebsiteLead: 'Cost per website lead',
  costPerContentView: 'Cost per content view',
  costPerLandingPageView: 'Cost per landing page view',
};

// ---------------------------------------------------------------------------
// Rows in
// ---------------------------------------------------------------------------

/** One (bucket, currency) row off the panels query, for ONE period. */
export interface AutoBucketRow {
  bucket: MetaAutoBucket;
  currency: string;
  spend: number;
  metaLeads: number;
  websiteLeads: number;
  contentViews: number;
  contacts: number;
  landingPageViews: number;
  impressions: number;
}

/** One currency of the headline query — the witness. No bucket, no join, no CASE. */
export interface AutoTotalsRow {
  currency: string;
  spend: number;
  metaLeads: number;
  websiteLeads: number;
  contentViews: number;
  contacts: number;
  landingPageViews: number;
  impressions: number;
}

/** One (day, bucket, currency) row off the series query. */
export interface AutoSeriesRow extends AutoBucketRow {
  date: string;
}

/** One campaign (per currency) off the campaigns query. */
export interface AutoCampaignInput {
  campaignId: string;
  name: string | null;
  bucket: MetaAutoBucket;
  goalBucket: MetaAutoBucket;
  currency: string;
  spend: number;
  metaLeads: number;
  websiteLeads: number;
  contentViews: number;
}

/** One ad off the creatives query. */
export interface AutoCreativeInput {
  adId: string;
  accountId: string;
  bucket: MetaAutoBucket;
  adName: string | null;
  campaignName: string | null;
  currency: string;
  spend: number;
  metaLeads: number;
  websiteLeads: number;
  contentViews: number;
  landingPageViews: number;
  status: string | null;
  thumbnailUrl: string | null;
}

/**
 * A bucket off the wire. ⚠️ An unrecognised value lands in `other` — the safe
 * default — rather than being dropped, which would break the reconciliation
 * silently. (The SQL CASE can only emit the keys; this is the second line.)
 */
export function autoBucketOrOther(v: unknown): MetaAutoBucket {
  return isMetaAutoBucket(v) ? v : 'other';
}

// ---------------------------------------------------------------------------
// ⚠️ THE PII GUARD — an ad or campaign name can be anything a person typed into
// Ads Manager, and one MCT ad was named with a person's email address (Phase 0
// M7). Card titles must never print a raw name that looks like an email or a
// phone number.
// ---------------------------------------------------------------------------

/** True when a name looks like an email address or a phone number. Deliberately over-eager. */
export function looksLikePersonalDetail(name: string): boolean {
  if (/[^\s@]+@[^\s@]+/.test(name)) return true;
  // A phone number: 8+ digits once spaces, dashes, brackets and a leading + are ignored.
  // ⚠️ Digits only count when the whole run is phone-shaped, so a campaign named
  // "Sale 2026" or "EOFY 30% Off" (few digits) is never mistaken for one.
  if (/(?:\+?\d[\d\s().-]{6,}\d)/.test(name) && name.replace(/\D/g, '').length >= 8) return true;
  return false;
}

/** The format word is unknown to us (format was CUT from the report, not deferred), so the fallback says "Ad". */
export function safeCreativeTitle(adName: string | null, campaignName: string | null): string {
  const ad = (adName ?? '').trim();
  if (ad && !looksLikePersonalDetail(ad)) return ad;
  const campaign = (campaignName ?? '').trim();
  if (campaign && !looksLikePersonalDetail(campaign)) return `${campaign} · Ad`;
  return 'Ad';
}

/** A campaign name for a table row, through the same guard. */
export function safeCampaignName(name: string | null): string {
  const n = (name ?? '').trim();
  return n && !looksLikePersonalDetail(n) ? n : 'Campaign';
}

// ---------------------------------------------------------------------------
// Assembly
// ---------------------------------------------------------------------------

const round2 = (n: number): number => Math.round(n * 100) / 100;

export function emptyAutoCounts(): AutoCounts {
  return {
    metaLeads: 0,
    websiteLeads: 0,
    leads: 0,
    contentViews: 0,
    contacts: 0,
    landingPageViews: 0,
    impressions: 0,
  };
}

/** Cost per X — null for X = 0 (never a cost-per of nothing, never infinity). */
export function autoCostPerOrNull(spend: number, count: number): number | null {
  return count > 0 ? round2(spend / count) : null;
}

type CountSource = Pick<
  AutoBucketRow,
  'metaLeads' | 'websiteLeads' | 'contentViews' | 'contacts' | 'landingPageViews' | 'impressions'
>;

/** The count for a key off one row. ⚠️ `leads` is DERIVED here, in one place. */
export function autoCountOf(r: CountSource, key: AutoCountKey): number {
  return key === 'leads' ? r.metaLeads + r.websiteLeads : r[key];
}

function sumCounts(rows: readonly CountSource[]): AutoCounts {
  const c = emptyAutoCounts();
  for (const r of rows) {
    c.metaLeads += r.metaLeads;
    c.websiteLeads += r.websiteLeads;
    c.contentViews += r.contentViews;
    c.contacts += r.contacts;
    c.landingPageViews += r.landingPageViews;
    c.impressions += r.impressions;
  }
  c.leads = c.metaLeads + c.websiteLeads;
  return c;
}

/** Whether a set of counts / spend has anything at all in it. */
export function autoHasActivity(counts: AutoCounts, spend: number): boolean {
  return (
    spend > 0 ||
    counts.metaLeads > 0 ||
    counts.websiteLeads > 0 ||
    counts.contentViews > 0 ||
    counts.contacts > 0 ||
    counts.landingPageViews > 0 ||
    counts.impressions > 0
  );
}

function moneyFor(rows: readonly AutoBucketRow[], spec: AutoPanelSpec): AutoMoney[] {
  // ⚠️ MONEY IS PER CURRENCY, and each cost-per divides a currency's spend by THAT
  // currency's own count — never a blended figure.
  const byCurrency = new Map<string, AutoBucketRow>();
  for (const r of rows) {
    const cur = byCurrency.get(r.currency);
    byCurrency.set(
      r.currency,
      cur
        ? {
            ...cur,
            spend: cur.spend + r.spend,
            metaLeads: cur.metaLeads + r.metaLeads,
            websiteLeads: cur.websiteLeads + r.websiteLeads,
            contentViews: cur.contentViews + r.contentViews,
            contacts: cur.contacts + r.contacts,
            landingPageViews: cur.landingPageViews + r.landingPageViews,
            impressions: cur.impressions + r.impressions,
          }
        : { ...r },
    );
  }
  return [...byCurrency.values()]
    .sort((a, b) => b.spend - a.spend)
    .map((c) => {
      const costPer: AutoMoney['costPer'] = {};
      for (const cp of spec.costPer) costPer[cp.key] = autoCostPerOrNull(c.spend, autoCountOf(c, cp.over));
      return { currency: c.currency, spend: round2(c.spend), costPer };
    });
}

/**
 * Build the six panels. ⚠️ ALWAYS SIX, in panel order: a bucket with no rows is
 * zeros rather than absent, so the reconciliation always has six terms. Whether a
 * panel is DRAWN is `autoPanelVisible`, decided from the spec.
 */
export function buildAutoPanels(
  rows: readonly AutoBucketRow[],
  comparisonRows: readonly AutoBucketRow[] | null,
  campaigns: readonly AutoCampaignInput[],
  creatives: readonly AutoCreativeInput[],
  charts: Partial<Record<MetaAutoBucket, AutoChartFamily[]>> = {},
  creativeLimit = 24,
): PortalAutoPanel[] {
  return META_AUTO_BUCKETS.map((bucket) => {
    const spec = autoPanelSpecFor(bucket);
    const mine = rows.filter((r) => r.bucket === bucket);
    const counts = sumCounts(mine);
    const money = moneyFor(mine, spec);
    const theirs = (comparisonRows ?? []).filter((r) => r.bucket === bucket);
    const compCounts = sumCounts(theirs);
    const compSpend = theirs.reduce((a, r) => a + r.spend, 0);
    return {
      bucket,
      counts,
      money,
      campaigns: buildCampaignRows(campaigns.filter((c) => c.bucket === bucket)),
      charts: charts[bucket] ?? [],
      ...buildPanelCreatives(
        creatives.filter((c) => c.bucket === bucket),
        spec.creativeMetric,
        creativeLimit,
      ),
      activeInComparison: autoHasActivity(compCounts, compSpend),
    };
  });
}

function buildCampaignRows(rows: readonly AutoCampaignInput[]): AutoCampaignRow[] {
  return rows
    .map((c) => ({
      campaignId: c.campaignId,
      // ⚠️ Through the PII guard: a campaign can be named by a person typing anything.
      name: safeCampaignName(c.name),
      currency: c.currency,
      spend: round2(c.spend),
      metaLeads: c.metaLeads,
      websiteLeads: c.websiteLeads,
      contentViews: c.contentViews,
      goalBucket: c.goalBucket,
    }))
    .sort((a, b) => b.spend - a.spend);
}

type MergedAutoCreative = AutoCreativeInput & { mergedAdCount: number };

/**
 * ⚠️⚠️ MERGE CREATIVES ACROSS ADS (Round 5 §D, PRESENTATION ONLY). One creative is
 * routinely trafficked as several ads across near-identical campaigns, so a panel
 * would show the same card three times at three different costs. Merging uses the
 * report's EXISTING rule through `creativeIdentityKey` — same club, identical ad
 * name, identical campaign name after one trailing version token, same currency —
 * and nothing else; an unnamed ad never merges. Counts and spend are SUMMED, so every
 * cost-per is re-derived from the combined figures at render, never averaged.
 * Status is `resolveMergedStatus` (active if any is). The thumbnail is the first
 * merged row's that has one (the rows arrive spend-descending).
 *
 * ⚠️ Nothing that feeds a headline, panel total or chart goes through this.
 */
export function mergeAutoCreatives(rows: readonly AutoCreativeInput[]): MergedAutoCreative[] {
  const groups: AutoCreativeInput[][] = [];
  const byKey = new Map<string, AutoCreativeInput[]>();
  for (const row of rows) {
    const key = creativeIdentityKey(row);
    if (key === null) {
      groups.push([row]);
      continue;
    }
    const existing = byKey.get(key);
    if (existing) existing.push(row);
    else {
      const g = [row];
      byKey.set(key, g);
      groups.push(g);
    }
  }
  return groups.map((g) => {
    const first = g[0] as AutoCreativeInput;
    const sum = (k: 'spend' | 'metaLeads' | 'websiteLeads' | 'contentViews' | 'landingPageViews') =>
      g.reduce((a, r) => a + r[k], 0);
    return {
      ...first,
      spend: sum('spend'),
      metaLeads: sum('metaLeads'),
      websiteLeads: sum('websiteLeads'),
      contentViews: sum('contentViews'),
      landingPageViews: sum('landingPageViews'),
      status: resolveMergedStatus(g.map((r) => r.status)),
      thumbnailUrl: g.find((r) => r.thumbnailUrl)?.thumbnailUrl ?? null,
      mergedAdCount: g.length,
    };
  });
}

/**
 * Rank one panel's creatives by that panel's primary metric (ties by spend), cap
 * them, and report the TOTAL so "See all N" is honest about what it opens.
 * ⚠️ The cap is the QUERY's row limit made visible, never a silent truncation.
 */
export function buildPanelCreatives(
  rows: readonly AutoCreativeInput[],
  metric: AutoCountKey | 'spend',
  limit: number,
): { creatives: AutoCreativeCard[]; creativesTotal: number } {
  const score = (r: AutoCreativeInput): number => {
    switch (metric) {
      case 'spend':
        return r.spend;
      case 'leads':
        return r.metaLeads + r.websiteLeads;
      case 'metaLeads':
      case 'websiteLeads':
      case 'contentViews':
      case 'landingPageViews':
        return r[metric];
      // ⚠️ Not carried per creative (the query does not read them), so a spec
      // asking to rank by one ranks everything equally and falls back to spend.
      case 'contacts':
      case 'impressions':
        return 0;
    }
  };
  const ranked = mergeAutoCreatives(rows).sort((a, b) => score(b) - score(a) || b.spend - a.spend);
  return {
    creativesTotal: ranked.length,
    creatives: ranked.slice(0, limit).map((r) => ({
      adId: r.adId,
      accountId: r.accountId,
      // ⚠️ THE PII GUARD — never a raw ad name.
      title: safeCreativeTitle(r.adName, r.campaignName),
      thumbnailUrl: r.thumbnailUrl,
      status: r.status,
      currency: r.currency,
      spend: round2(r.spend),
      metaLeads: r.metaLeads,
      websiteLeads: r.websiteLeads,
      contentViews: r.contentViews,
      landingPageViews: r.landingPageViews,
      mergedAdCount: r.mergedAdCount,
    })),
  };
}

export interface AutoHeadlineInput {
  totals: readonly AutoTotalsRow[];
  /** The comparison period's totals, or null when none was requested. */
  comparisonTotals: readonly AutoTotalsRow[] | null;
}

export function buildAutoHeadline(input: AutoHeadlineInput): PortalAutoHeadline {
  const c = sumCounts(input.totals);
  const spend = new Map<string, number>();
  for (const t of input.totals) spend.set(t.currency, (spend.get(t.currency) ?? 0) + t.spend);
  return {
    metaLeads: c.metaLeads,
    websiteLeads: c.websiteLeads,
    leads: c.leads,
    contentViews: c.contentViews,
    contacts: c.contacts,
    landingPageViews: c.landingPageViews,
    impressions: c.impressions,
    metaSpend: [...spend.entries()]
      .map(([currency, s]) => ({ currency, spend: round2(s) }))
      .sort((a, b) => b.spend - a.spend),
    contactsComparison: sumCounts(input.comparisonTotals ?? []).contacts,
  };
}

// ---------------------------------------------------------------------------
// ⚠️⚠️ VISIBILITY — hide-when-empty, decided here and nowhere else.
// ---------------------------------------------------------------------------

/**
 * Whether a panel is drawn. `always` panels (AIA, Meta lead) always render, with
 * an empty-state sentence when nothing ran. `when_active` panels render only when
 * they have something in the SELECTED period or the COMPARISON period (round 4 §4):
 * a panel that is empty now but had results in the comparison period still shows,
 * so a drop to nothing is visible rather than the panel silently vanishing.
 */
export function autoPanelVisible(panel: PortalAutoPanel): boolean {
  const spec = autoPanelSpecFor(panel.bucket);
  if (spec.render === 'always') return true;
  const spend = panel.money.reduce((a, m) => a + m.spend, 0);
  return autoHasActivity(panel.counts, spend) || panel.activeInComparison;
}

/** Whether the panel's own empty-state sentence is the thing to draw instead of figures. */
export function autoPanelIsEmpty(panel: PortalAutoPanel): boolean {
  return !autoHasActivity(
    panel.counts,
    panel.money.reduce((a, m) => a + m.spend, 0),
  );
}

/**
 * ⚠️ CONTACT IS HIDDEN EVERYWHERE WHEN IT IS ZERO IN BOTH PERIODS (round 4 §3, and
 * Scott's decision 1). One answer for the headline tile, the home strip card and
 * every per-panel extra, so they cannot disagree. A contact count in only the
 * comparison period still shows, so a drop to zero is visible.
 */
export function contactsVisible(headline: PortalAutoHeadline): boolean {
  return headline.contacts > 0 || headline.contactsComparison > 0;
}

/** A panel's extras with Contact removed when it is hidden. */
export function visibleExtras(spec: AutoPanelSpec, showContacts: boolean): AutoCountKey[] {
  return spec.extras.filter((k) => k !== 'contacts' || showContacts);
}

// ---------------------------------------------------------------------------
// The charts — built on the SHARED series core, so the gap / partial-month /
// never-a-zero rules are the report's, not a copy of them.
// ---------------------------------------------------------------------------

export interface AutoSeriesOptions {
  range: { from: string; to: string };
  grouping: PortalGrouping;
  today: string;
  coverage: PortalCoverage;
}

function monthsWindowFrom(today: string): string {
  return `${yearOnYearMonths(today)[0] as string}-01`;
}

/** The date range the series query must cover: the period ∪ twelve months. */
export function autoSeriesRange(
  range: { from: string; to: string },
  today: string,
): { from: string; to: string } {
  const monthsFrom = monthsWindowFrom(today);
  return {
    from: range.from < monthsFrom ? range.from : monthsFrom,
    to: range.to > today ? range.to : today,
  };
}

/**
 * One chart family from rows. ⚠️ THE COUNT RIDES IN `leads` and the cost-per in
 * `costPerLead` (the goal-split convention — the card's `goal` source reads
 * them), and the card is told what they really are through its `labels`.
 *
 * ⚠️ A family whose rows span MORE THAN ONE CURRENCY carries no money at all —
 * spend and cost-per nulled, `costPer: null` — rather than a blended line. A
 * family with no cost-per spec (the all-campaign content-view chart) carries none
 * either: a cost per content view over mixed-goal spend is not an honest figure.
 */
export function buildAutoFamily(
  rows: readonly AutoSeriesRow[],
  spec: AutoChartSpec,
  opts: AutoSeriesOptions,
): AutoChartFamily {
  const monthsFrom = monthsWindowFrom(opts.today);
  const currencies = new Set(rows.map((r) => r.currency));
  const singleCurrency = currencies.size <= 1;
  const costPer = singleCurrency && spec.costPer ? spec.costPer : null;
  // ⚠️ A cost-per whose denominator is not the plotted count would be plotted
  // against the wrong series. Every spec today has them equal, so this is a guard
  // against a future edit rather than a live case.
  const consistent = costPer === null || costPer.over === spec.metric;
  const measured: MeasuredBucket[] = rows.map((r) => ({
    bucket: r.date,
    leads: autoCountOf(r, spec.metric),
    // ⚠️ SPEND IS PLOTTED WHENEVER THE ROWS ARE ONE CURRENCY, with or without a
    // cost-per (round 5 §A2: every card carries a Spend chip). Raw spend is honest
    // over mixed-goal campaigns; a cost PER CONTENT VIEW over them is not.
    spend: singleCurrency ? r.spend : 0,
    impressions: 0,
    clicks: 0,
  }));
  const within = (from: string, to: string) => measured.filter((m) => m.bucket >= from && m.bucket <= to);
  const withMoney = costPer !== null && consistent;
  const withSpend = singleCurrency;
  const shape = (points: ReturnType<typeof buildSeries>) =>
    points.map((p) => ({
      ...p,
      impressions: null,
      clicks: null,
      ...(withMoney ? {} : withSpend ? { costPerLead: null } : { spend: null, costPerLead: null }),
    }));
  return {
    key: spec.key,
    metric: spec.metric,
    costPer: withMoney ? (costPer as AutoCostPerSpec).key : null,
    // ⚠️ Non-null whenever SPEND is plotted (the card's Spend chip keys off it).
    currency: withSpend ? ([...currencies][0] ?? null) : null,
    period: shape(
      buildSeries({
        from: opts.range.from,
        to: opts.range.to,
        grouping: opts.grouping,
        measured: within(opts.range.from, opts.range.to),
        coverage: opts.coverage,
        today: opts.today,
      }),
    ),
    months: shape(
      buildSeries({
        from: monthsFrom,
        to: opts.today,
        grouping: 'monthly',
        measured: within(monthsFrom, opts.today),
        coverage: opts.coverage,
        today: opts.today,
      }),
    ),
  };
}

/** The headline-level charts (Home, and the Meta page's all-campaigns card). */
export const AUTO_HEADLINE_CHARTS: readonly AutoChartSpec[] = [
  { key: 'leads', metric: 'leads', costPer: COST.perLeadAll },
  // ⚠️ NO cost-per: spend across mixed-goal campaigns divided by content views is not a cost of anything.
  { key: 'contentViews', metric: 'contentViews', costPer: null },
];

/** Every panel's families plus the headline's, from ONE row set. */
export function buildAutoCharts(
  rows: readonly AutoSeriesRow[],
  opts: AutoSeriesOptions,
): { byBucket: Partial<Record<MetaAutoBucket, AutoChartFamily[]>>; headline: AutoChartFamily[] } {
  const byBucket: Partial<Record<MetaAutoBucket, AutoChartFamily[]>> = {};
  for (const spec of AUTO_PANELS) {
    if (spec.charts.length === 0) continue;
    const mine = rows.filter((r) => r.bucket === spec.bucket);
    byBucket[spec.bucket] = spec.charts.map((c) => buildAutoFamily(mine, c, opts));
  }
  const headline = AUTO_HEADLINE_CHARTS.map((c) => buildAutoFamily(rows, c, opts));
  return { byBucket, headline };
}

// ---------------------------------------------------------------------------
// ⚠️⚠️ THE RECONCILIATION — checked against an INDEPENDENTLY queried headline.
// ---------------------------------------------------------------------------

export interface AutoReconcileMismatch {
  measure: 'metaLeads' | 'websiteLeads' | 'contentViews' | 'contacts' | 'spend';
  currency: string | null;
  panels: number;
  headline: number;
}

const COUNT_TOLERANCE = 1e-6;
/** Spend is rounded to the cent per panel (six) and in the headline: honest rounding can differ by 7 x half a cent. */
const SPEND_TOLERANCE = 0.04;

/**
 * Σ panels == headline on spend (PER CURRENCY, never blended), metaLeads,
 * websiteLeads, contentViews and contacts. ⚠️ The headline must come from the
 * SEPARATE totals query: passing one built from the panels would make this
 * `x == Σx` and it could never fail.
 */
export function reconcileAutomotive(
  panels: readonly PortalAutoPanel[],
  headline: PortalAutoHeadline,
): AutoReconcileMismatch[] {
  const out: AutoReconcileMismatch[] = [];
  const sum = (k: AutoCountKey): number => panels.reduce((a, p) => a + p.counts[k], 0);
  const counts: Array<['metaLeads' | 'websiteLeads' | 'contentViews' | 'contacts', number]> = [
    ['metaLeads', headline.metaLeads],
    ['websiteLeads', headline.websiteLeads],
    ['contentViews', headline.contentViews],
    ['contacts', headline.contacts],
  ];
  for (const [k, h] of counts) {
    const p = sum(k);
    if (Math.abs(p - h) > COUNT_TOLERANCE) out.push({ measure: k, currency: null, panels: p, headline: h });
  }
  const panelSpend = new Map<string, number>();
  for (const p of panels) {
    for (const m of p.money) panelSpend.set(m.currency, (panelSpend.get(m.currency) ?? 0) + m.spend);
  }
  const currencies = new Set([...panelSpend.keys(), ...headline.metaSpend.map((s) => s.currency)]);
  for (const c of currencies) {
    const p = panelSpend.get(c) ?? 0;
    const h = headline.metaSpend.find((s) => s.currency === c)?.spend ?? 0;
    if (Math.abs(p - h) > SPEND_TOLERANCE) out.push({ measure: 'spend', currency: c, panels: p, headline: h });
  }
  return out;
}

export function buildAutomotiveReport(input: {
  rows: readonly AutoBucketRow[];
  comparisonRows: readonly AutoBucketRow[] | null;
  campaigns: readonly AutoCampaignInput[];
  creatives: readonly AutoCreativeInput[];
  headline: AutoHeadlineInput;
  charts?: { byBucket: Partial<Record<MetaAutoBucket, AutoChartFamily[]>>; headline: AutoChartFamily[] };
}): { report: PortalMetaAutomotiveReport; mismatches: AutoReconcileMismatch[] } {
  const panels = buildAutoPanels(
    input.rows,
    input.comparisonRows,
    input.campaigns,
    input.creatives,
    input.charts?.byBucket ?? {},
  );
  const headline = buildAutoHeadline(input.headline);
  const mismatches = reconcileAutomotive(panels, headline);
  return {
    report: {
      headline,
      panels,
      reconciles: mismatches.length === 0,
      hasComparison: input.comparisonRows !== null,
      charts: input.charts?.headline ?? [],
    },
    mismatches,
  };
}
